import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components

import AlertInfo from '@components/common/alert/AlertInfo';
import Form from '@lib/wrappers/Form';
import OtherMailingAddresses from '@components/ssap/pages/pageIncludes/common/address/OtherMailingAddresses';
import PageHeader from '../headers/PageHeader';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators / Selectors

import { changeData } from '@actions/ssap/ssapActionCreators';
import { showModal } from '@actions/common/actionCreators';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { dataSyncingTools } from '@utils/tools/dataSyncingTools';
import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { normalizeAddress } from '@utils/normalize/normalizeAddress';
import { preventerTools } from '@utils/tools/preventerTools';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { ssapModalTypes as MODAL_TYPES } from '@constants/ssap/modals/ssapModalTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { STATE_NAMES } from '@constants/configs/states';
import { selectIfLivingArrangement } from '@reducers/appReducer';


/**
 *
 * @param {Object} props
 * @returns {React.Component} = 'Living separately with Head of household' Page
 *
 */
const Page2_11_mod2  = props => {

    const {
        counties,
        data,
        data: {
            taxHousehold: {
                otherAddresses: allAddresses = [],
                householdMember: householdMembers = [],
                thCommonParams: {
                    hasSomeoneWithAnotherHomeAddress,
                    hasSomeoneWithAnotherMailingAddress
                }
            }
        },
        form,
        environment: {
            stateCode
        }
    } = props;

    const {
        addressDefaults: {
            defaultHomeAddress,
            defaultMailingAddress,
            defaultHomeAddressId,
            defaultMailingAddressId
        },
        formName,
        itfFeatureEnabled,
        pageName
    } = props;
    
    const livingArrangementEnabled = useSelector(selectIfLivingArrangement);

    const primaryContact = householdMemberTools.getPrimaryContact(householdMembers);
    
    const {
        householdContact: {
            homeAddress,
            mailingAddress: primaryContactMailingAddress
        },
        applicantGuid: primaryApplicantGuid
    } = primaryContact;

    const {
        isFormSubmitted,
        isEligiblyForSave
    } = preventerTools.provideObject(form[formName]);

    const [ areOtherMailingAddressesValid, toggleMailingAddressesValidation ] = useState(true);

    const otherAddresses = itfFeatureEnabled
        ? data
            .taxHousehold
            .otherAddresses
            .reduce((addresses, address, indexInOtherAddresses) => {
                const { deletable } = address;
                return !deletable
                    ? addresses
                    : [
                        ...addresses,
                        {
                            ...address,
                            indexInOtherAddresses
                        }
                    ];
            }, [])
        : data
            .taxHousehold
            .otherAddresses
            .filter((a, i) => i > 0);

    useEffect(() => {

        const newState = { ...data };

        newState.taxHousehold.householdMember =
            newState.taxHousehold.householdMember.reduce((hhms, hhm) => {
                return hhm.applicantGuid !== primaryApplicantGuid
                    ? [
                        ...hhms,
                        {
                            ...hhm,
                            livingArrangement: !hhm.livesAtOtherAddressIndicator ? primaryContact.livingArrangement : hhm.livingArrangement,
                            householdContact: {
                                ...hhm.householdContact,
                                homeAddress: !hhm.livesAtOtherAddressIndicator
                                    ? (
                                        itfFeatureEnabled
                                            ? {
                                                ...primaryContact.householdContact.homeAddress,
                                                deletable: false
                                            }
                                            : primaryContact.householdContact.homeAddress
                                    )
                                    : hhm.householdContact.homeAddress,
                                ...(
                                    itfFeatureEnabled
                                        ? {
                                            mailingAddress: !hhm.hasAnotherMailingAddress
                                                ? {
                                                    ...primaryContact.householdContact.mailingAddress,
                                                    deletable: false
                                                }
                                                : hhm.householdContact.mailingAddress
                                        }
                                        : {
                                            mailingAddress: primaryContact.householdContact.mailingAddress
                                        }
                                )
                            }
                        }
                    ]
                    : [ ...hhms, hhm ];
            }, []);

        props.actions.changeData(newState);
    }, [ ]);

    const onFieldChange = (...args) => {

        let [ newData, indexInArray, , [ , value ] ] = args;

        const newState = { ...data };

        if(!value) {
            (hhm => {
                hhm.addressId = 1;
                hhm.otherAddress.livingOutsideofStateTemporarilyIndicator = null;
                hhm.householdContact.homeAddress = allAddresses.find(a => a.addressId === 1);
            })(newData);
        }

        const currentState = newData.otherAddress.address.state;

        if (currentState !== householdMembers[indexInArray].otherAddress.address.state) {
            newData.otherAddress.address.countyCode = null;
            newData.otherAddress.address.county = null;
        }

        const isLivingTheSameAddress = newData.livesAtOtherAddressIndicator === false;

        if (isLivingTheSameAddress) {
            newData = {
                ...cloneObject(newData),
                householdContact: {
                    ...newData.householdContact,
                    homeAddress: primaryContact.householdContact.homeAddress
                },
                livingArrangement: primaryContact.livingArrangement,
                livesWithHouseholdContactIndicator: true,
                otherAddress: cloneObject(EMPTY_OBJECTS.OTHER_ADDRESS),
            };
        } else {
            newData = {
                ...cloneObject(newData),
                householdContact: {
                    ...newData.householdContact,
                    homeAddress: cloneObject(EMPTY_OBJECTS.HOUSEHOLD_CONTACT.homeAddress)
                },
                addressId: defaultHomeAddressId,
                livingArrangement: 'UNSPECIFIED',
                livesWithHouseholdContactIndicator: false
            };
        }

        newState.taxHousehold.householdMember[indexInArray] = newData;

        const syncedData = dataSyncingTools.syncThCommonProps(
            newState,
            { propName: 'homeAddress' }
        );

        props.actions.changeData(syncedData);
    };

    const onFieldChange_livingArrangement = (...args) => {
        let [newData, indexInArray] = args;
        const newState = { ...data };
        newState.taxHousehold.householdMember[indexInArray] = newData;
        props.actions.changeData(newState);
    };

    const onFieldChange_outside = (newData, indexInArray) => {
        const isLivingOutside = newData.otherAddress.livingOutsideofStateTemporarilyIndicator === false;

        if(isLivingOutside) {
            newData.otherAddress.livingOutsideofStateTemporarilyAddress =
                cloneObject(EMPTY_OBJECTS.OTHER_ADDRESS.livingOutsideofStateTemporarilyAddress);
        }

        const newState = { ...data };

        newState.taxHousehold.householdMember[indexInArray] = newData;

        props.actions.changeData(newState);
    };

    const onFieldChange_none_of_above = (...args)=> {

        const [ newData ] = args;

        const updateIndividualValues = hhm => {
            return {
                ...hhm,
                addressId: 1,
                livesWithHouseholdContactIndicator: true,
                livesAtOtherAddressIndicator: false,
                livingArrangement: primaryContact.livingArrangement,
                otherAddress: cloneObject(EMPTY_OBJECTS.OTHER_ADDRESS),
                householdContact: {
                    ...hhm.householdContact,
                    homeAddress: primaryContact.householdContact.homeAddress
                }
            };
        };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newData,
            {
                customUpdater: updateIndividualValues,
                isCommon: true,
                propName: 'homeAddress'
            }
        );

        props.actions.changeData(syncedData);
    };

    const handleAdding = e => {

        e.preventDefault();

        props.actions.showModal({
            modalType: MODAL_TYPES.OTHER_ADDRESS_MODAL,
            modalData: {
                otherAddress: {
                    postalCode: null,
                    countyCode: null,
                    county: null,
                    primaryAddressCountyFipsCode: null,
                    streetAddress1: null,
                    streetAddress2: null,
                    state: null,
                    city: null,
                    ...(
                        itfFeatureEnabled
                            ? {
                                addressId: otherAddresses.length + 3,
                                deletable: true
                            } : {
                                addressId: otherAddresses.length + 2
                            }
                    )
                },
                index: null,
                pageName
            }
        });
    };

    const handleEditing = e => {

        const {
            name
        } = e.target;

        e.preventDefault();

        const index = Number(name);

        const otherAddress = data.taxHousehold.otherAddresses[index];

        props.actions.showModal({
            modalType: MODAL_TYPES.OTHER_ADDRESS_MODAL,
            modalData: {
                ...(
                    itfFeatureEnabled
                        ? {
                            defaultMailingAddressId,
                            itfFeatureEnabled
                        }
                        : {}
                ),
                otherAddress,
                index,
                pageName
            }
        });
    };

    const handleDeleting = e => {
        const {
            name,
            dataset: {
                id
            }
        } = e.target;

        e.preventDefault();

        const index = Number(name);
        const deletedAddressId = Number(id);

        const newState = { ...data };

        newState
            .taxHousehold
            .otherAddresses
            .splice(index, 1);

        newState
            .taxHousehold
            .otherAddresses
            .forEach(address => {
                if(address.addressId > index) {
                    address.addressId = address.addressId - 1;
                }
            });

        if(itfFeatureEnabled) {
            const [
                ,
                { addressId: defaultMailingAddressIdAfterDeletion } = {}
            ] = newState.taxHousehold.otherAddresses.filter(a => !a.deletable);

            newState
                .taxHousehold
                .householdMember
                .forEach(hhm => {
                    if(hhm.addressId === deletedAddressId) {
                        hhm.addressId = defaultHomeAddressId;
                        hhm.householdContact.homeAddress = defaultHomeAddress;
                    }
                    if(hhm.mailingAddressId === deletedAddressId) {
                        hhm.mailingAddressId = defaultMailingAddressIdAfterDeletion;
                        hhm.householdContact.mailingAddress = defaultMailingAddress;
                    }
                    if(hhm.addressId > deletedAddressId) {
                        hhm.addressId = hhm.addressId - 1;
                    }
                    if(hhm.mailingAddressId > deletedAddressId) {
                        hhm.mailingAddressId = hhm.mailingAddressId - 1;
                    }
                });
        } else {
            newState
                .taxHousehold
                .householdMember
                .forEach(hhm => {
                    if(hhm.addressId - 1 === index) {
                        hhm.addressId = 1;
                    }
                    if(hhm.addressId - 1 > index) {
                        hhm.addressId = hhm.addressId - 1;
                    }
                });
        }

        props.actions.changeData(newState);
    };

    const onFieldChange_address = (...args) => {

        const [ , [ hhmIndex, newAddressId ], , [ , value ]] = args;

        const newState = { ...data };

        if(value) {
            (hhm => {
                hhm.addressId = newAddressId;
                hhm.otherAddress.livingOutsideofStateTemporarilyIndicator = null;
                hhm.householdContact.homeAddress = otherAddresses.find(a => a.addressId === newAddressId);
            })(newState.taxHousehold.householdMember[hhmIndex]);
        } else {
            (hhm => {
                hhm.addressId = 1;
                hhm.otherAddress.livingOutsideofStateTemporarilyIndicator = null;
                hhm.householdContact.homeAddress = allAddresses.find(a => a.addressId === 1);
            })(newState.taxHousehold.householdMember[hhmIndex]);
        }

        props.actions.changeData(newState);
    };

    const getHouseholdMembers = householdMembers => {
        const members = [];

        householdMembers.forEach((member, i) => {
            const {
                applicantGuid,
                livesAtOtherAddressIndicator,
                livingArrangement = 'UNSPECIFIED',
                otherAddress: {
                    livingOutsideofStateTemporarilyIndicator,
                },
                name,
                addressId
            } = member;

            const checkIfPersonLivesOutsideState = () => {
                const address = otherAddresses.find(address => address.addressId === addressId);
                const stateCodeFromAddress = address ? address.state : null;
                return stateCodeFromAddress !== stateCode && addressId !== 1;
            };

            if (i !== 0) {
                members.push(
                    <li key = { applicantGuid } >
                        <span className = 'usa-sr-only' >
                            Check if { normalizeNameOfPerson(name) } live at address other than the stated address
                        </span>
                        <UniversalInput
                            label = { {
                                text: 'common.input.label.non_internationalized_text',
                                labelTextReplacements: {
                                    text: normalizeNameOfPerson(name)
                                }
                            } }
                            inputData = { {
                                isInputRequired: false,
                                type: INPUT_TYPES.CHECKBOX,
                                classes: [],
                                name: 'livesAtOtherAddressIndicator',
                                checked: livesAtOtherAddressIndicator,
                                value: livesAtOtherAddressIndicator,
                                currentData: member,
                                fields: [],
                            } }
                            inputActions = { {
                                onFieldChange
                            } }
                            indexInArray = { i }
                        />

                        {

                            livesAtOtherAddressIndicator &&

                            <Fragment>
                                {
                                    otherAddresses.length === 0 &&
                                        <FormattedMessage
                                            id = 'ssap.start.addresses.provide_address'
                                            tagName = 'p'
                                        />
                                }

                                {
                                    otherAddresses.map((address, idx) => {

                                        const {
                                            indexInOtherAddresses,
                                            addressId: id
                                        } = address;

                                        return (
                                            <div
                                                className = 'grid-row address_l2'
                                                key = { `${ address.addressLine1 }_${ idx }` }
                                            >
                                                <UniversalInput
                                                    label = { {
                                                        text: 'common.input.label.non_internationalized_text',
                                                        labelTextReplacements: {
                                                            text: normalizeAddress(address)
                                                        }
                                                    } }
                                                    inputData = { {
                                                        isInputRequired: false,
                                                        isValidationErrorMessageVisible: false,
                                                        type: INPUT_TYPES.CHECKBOX,
                                                        name: 'addressId',
                                                        classes: [ '' ],
                                                        checked: address.addressId === addressId,
                                                        value: address.addressId,
                                                        currentData: { addressId: address.addressId },
                                                        fields: [],
                                                        valueToPass: 'livesAtOtherAddressIndicator'
                                                    } }
                                                    inputActions = { {
                                                        onFieldChange: onFieldChange_address
                                                    } }
                                                    indexInArray = { [ i, address.addressId ] }
                                                />
                                                <div className='grid-row margin-b-10'>
                                                    <button
                                                        className = 'usa-button usa-button--outline gi-button gi-button__outline gi-button--small'
                                                        onClick = { handleDeleting }
                                                        {
                                                            ...(
                                                                itfFeatureEnabled
                                                                    ? {
                                                                        name: indexInOtherAddresses,
                                                                        'data-id': id
                                                                    } : {
                                                                        name: idx + 1
                                                                    }
                                                            )
                                                        }
                                                    >
                                                        <FormattedMessage id = 'button.delete' />
                                                    </button>
                                                    <button
                                                        className = 'usa-button usa-button--outline gi-button gi-button__outline gi-button--small'
                                                        onClick = { handleEditing }
                                                        {
                                                            ...(
                                                                itfFeatureEnabled
                                                                    ? {
                                                                        name: indexInOtherAddresses
                                                                    }
                                                                    : {
                                                                        name: idx + 1
                                                                    }
                                                            )
                                                        }
                                                    >
                                                        <FormattedMessage id = 'button.edit' />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                }

                                <button
                                    onClick={ handleAdding }
                                    className = 'usa-button usa-button--outline gi-button gi-button__outline gi-button--small'
                                >
                                    <FormattedMessage id = 'button.add_address' />
                                </button>

                                {
                                    livingArrangementEnabled && <UniversalInput
                                        errorClasses={['ml-0']}
                                        label={{
                                            text: 'ssap.input.label.living_arrangement_question',
                                            replacements: {
                                                personName: (
                                                    <strong>
                                                        {normalizeNameOfPerson(name)}
                                                    </strong>
                                                ),
                                            },
                                            labelClass: ['text-left', 'w-100', 'p-0', 'mb-2'],
                                            isLabelRequired: true
                                        }}
                                        dropdownContainerClassName='d-flex flex-column align-items-start'
                                        inputData={{
                                            isInputRequired: true,
                                            isInputStandalone: true,
                                            type: INPUT_TYPES.DROPDOWN,
                                            name: 'livingArrangement',
                                            value: livingArrangement,
                                            options: DROPDOWN_OPTIONS.LIVING_ARRANGEMENT,
                                            valuesToMatch: DROPDOWN_OPTIONS.LIVING_ARRANGEMENT_CODES,
                                            validationType: VALIDATION_TYPES.CHOSEN_FROM_LIST,
                                            currentData: member,
                                            fields: [],
                                        }}
                                        inputActions={{
                                            onFieldChange: onFieldChange_livingArrangement
                                        }}
                                        indexInArray={i}
                                    />
                                }

                                {
                                    checkIfPersonLivesOutsideState() &&

                                    <UniversalInput
                                        legend = { {
                                            legendText: 'ssap.input.legend.person_lives_outside',
                                            legendTextReplacements: {
                                                personName: (
                                                    <strong>
                                                        { normalizeNameOfPerson(name) }
                                                    </strong>
                                                ),
                                                stateName: STATE_NAMES[stateCode]
                                            }
                                        } }
                                        label = { {
                                            text: '',
                                            labelClass: [ 'grid-row' ],
                                            ulClass: [ '' ],
                                            showRequiredIcon: true
                                        } }
                                        inputData = { {
                                            isInputRequired: true,
                                            type: INPUT_TYPES.RADIO_BUTTONS,
                                            name: 'livingOutsideofStateTemporarilyIndicator',
                                            value: livingOutsideofStateTemporarilyIndicator,
                                            options: DROPDOWN_OPTIONS.YES_NO,
                                            validationType: VALIDATION_TYPES.CHOSEN,
                                            currentData: member,
                                            fields: [ 'otherAddress' ]
                                        } }
                                        errorClasses = { [ 'margin-l-0' ] }
                                        inputActions = { {
                                            onFieldChange : onFieldChange_outside
                                        } }
                                        indexInArray = { i }
                                    />
                                }
                            </Fragment>
                        }
                    </li>
                );
            }
        });

        members.push(
            <li key = 'hasSomeoneWithAnotherHomeAddress' >
                <UniversalInput
                    label = { {
                        text: 'ssap.input.label.none_of_above'
                    } }
                    inputData = { {
                        isInputRequired: false,
                        type: INPUT_TYPES.CHECKBOX,
                        name: 'hasSomeoneWithAnotherHomeAddress',
                        classes: [],
                        checked: hasSomeoneWithAnotherHomeAddress === false,
                        value: hasSomeoneWithAnotherHomeAddress === false,
                        currentData: cloneObject(data),
                        fields: [ 'taxHousehold', 'thCommonParams' ],
                        reversedValue: true
                    } }
                    inputActions = { {
                        onFieldChange: onFieldChange_none_of_above
                    } }
                />
            </li>
        );

        return members;
    };

    const atLeastOneLivesInState = useMemo(
        () => householdMembers.some(hhm => hhm.householdContact.homeAddress.state === stateCode),
        [ householdMembers.map(hhm => hhm.householdContact.homeAddress.state) ]
    );

    const areOtherAddressesProvided = useMemo(
        () => {
            let result = true;

            householdMembers.forEach((hhm, hhmIndex) => {
                if(hhmIndex !== 0 && hhm.livesWithHouseholdContactIndicator === false && hhm.addressId === 1) {
                    result = false;
                }
            });

            const areOtherAddresses = householdMembers.some(hhm => hhm.livesAtOtherAddressIndicator);

            return areOtherAddresses && result;
        },
        [ householdMembers.map(hhm => hhm.livesAtOtherAddressIndicator) ]
    );

    const restOfValidations = useMemo(
        () => {
            switch(hasSomeoneWithAnotherHomeAddress === false) {
                case true:
                    return true;
                case false:
                case null:
                    return areOtherAddressesProvided;
            }
        },
        [ areOtherAddressesProvided, hasSomeoneWithAnotherHomeAddress ]
    );

    const checkTopLevelValidation = () => {
        if(!atLeastOneLivesInState) {
            return false;
        }
        if(itfFeatureEnabled) {
            return restOfValidations && areOtherMailingAddressesValid;
        } else {
            return restOfValidations;
        }
    };

    const isFormValid = useMemo(
        () => checkTopLevelValidation(),
        [
            areOtherMailingAddressesValid,
            atLeastOneLivesInState,
            itfFeatureEnabled,
            restOfValidations
        ]
    );

    const getValidationErrorMessage = () => {
        let messageText = VALIDATION_ERROR_MESSAGES.ANY_VALUE;

        if(restOfValidations && !atLeastOneLivesInState) {
            messageText = VALIDATION_ERROR_MESSAGES.NOBODY_LIVES_IN_STATE;
        }

        return (
            <ValidationErrorMessage
                isVisible = { isFormSubmitted && !isEligiblyForSave && !isFormValid }
                messageText = { messageText }
                errorClasses = { [ 'margin-l-0' ] }
            />
        );
    };

    return (
        <Fragment>
            <div
                className = 'fade-in'
                id = 'fhAdditionalInfo'
            >
                <div className = 'subsection' >
                    <PageHeader
                        text = 'ssap.subheader.where_household_members_live'
                        headerClassName = 'gi-heading-alt'
                    />
                    <Form
                        topLevelValidity = { isFormValid }
                        { ...{ formName } }
                    >
                        <fieldset className = 'usa-fieldset' >
                            <legend className = 'required-true' >
                                <FormattedMessage
                                    id = 'ssap.input.legend.do_live_together'
                                    values = { {
                                        homeAddress: (
                                            <strong>
                                                {normalizeAddress(homeAddress, undefined, true)}
                                            </strong>
                                        ),
                                        strong: chunks => <strong>{chunks}</strong>
                                    } }
                                />
                            </legend>
                            <AlertInfo id = 'ssap.start.addresses.coverage_affect_alert' />
                            { getValidationErrorMessage() }
                            <ul className = 'usa-list usa-list--unstyled' >
                                {
                                    getHouseholdMembers(householdMembers, counties)
                                }
                            </ul>
                            {
                                itfFeatureEnabled
                                    ? (
                                        <OtherMailingAddresses
                                            {...{
                                                areOtherMailingAddressesValid,
                                                changeData: props.actions.changeData,
                                                data,
                                                defaultMailingAddress,
                                                defaultMailingAddressId,
                                                handleAdding,
                                                handleDeleting,
                                                handleEditing,
                                                hasSomeoneWithAnotherMailingAddress,
                                                householdMembers,
                                                isFormSubmitted,
                                                isEligiblyForSave,
                                                otherAddresses,
                                                primaryContactMailingAddress,
                                                toggleMailingAddressesValidation,
                                                underFormControl: true
                                            }}
                                        />
                                    )
                                    : null
                            }
                        </fieldset>
                    </Form>
                </div>
            </div>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    counties: state.ssap.counties,
    data: state.ssap.data,
    form: state.ssap.form
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        showModal
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_11_mod2);
